import { FieldTypes } from '@features/campaigns/builder/components/FormFields/FieldTypes.ts';
import { apiRoutes } from '@features/campaigns/builder/consts/apiRoutes.ts';
import { CreativeImageDimensions } from '@features/campaigns/builder/utils/areCreativeDimensionsValid.ts';
import { HighImpactCreativeType, HighImpactCreativeTypes } from '@features/campaigns/shared/types/campaign.ts';
import { CampaignId } from '@shared/types/campaign.ts';

export const HIGH_IMPACT_CREATIVE_SPECIFICATIONS_URL =
  'https://admanager.mediaset.it/app/uploads/2026/10/AdManager_specifiche-tecniche_display_pacchetto_tematico.pdf';

export type HighImpactCreativeFieldName =
  | typeof FieldTypes.CREATIVE_INTRO_MOBILE_IMAGE
  | typeof FieldTypes.CREATIVE_MASTHEAD_IMAGE
  | typeof FieldTypes.CREATIVE_MASTHEAD_ADAPTIVE_IMAGE
  | typeof FieldTypes.CREATIVE_SKIN_IMAGE;

export type HighImpactPoliticalContentFieldName =
  | typeof FieldTypes.INTRO_MOBILE_CONTAINS_POLITICAL_CONTENT
  | typeof FieldTypes.MASTHEAD_CONTAINS_POLITICAL_CONTENT
  | typeof FieldTypes.MASTHEAD_ADAPTIVE_CONTAINS_POLITICAL_CONTENT
  | typeof FieldTypes.SKIN_CONTAINS_POLITICAL_CONTENT;

export interface HighImpactCreativeFormat {
  type: HighImpactCreativeType;
  fieldName: HighImpactCreativeFieldName;
  politicalContentFieldName: HighImpactPoliticalContentFieldName;
  dimensions: CreativeImageDimensions;
  maxFileSize: number;
  translationKeyPrefix: string;
  testId: string;
  politicalContentTestId: string;
  politicalContentLabelTranslationKey: string;
  apiRoute: (campaignId: CampaignId) => string;
}

export const HIGH_IMPACT_CREATIVE_FORMATS: Record<HighImpactCreativeType, HighImpactCreativeFormat> = {
  [HighImpactCreativeTypes.INTRO_MOBILE]: {
    type: HighImpactCreativeTypes.INTRO_MOBILE,
    fieldName: FieldTypes.CREATIVE_INTRO_MOBILE_IMAGE,
    politicalContentFieldName: FieldTypes.INTRO_MOBILE_CONTAINS_POLITICAL_CONTENT,
    dimensions: { width: 640, height: 1096 },
    maxFileSize: 80 * 1024,
    translationKeyPrefix: 'builder.formFields.creativeIntroMobileImage',
    testId: 'field-creative-intro-mobile-image',
    politicalContentTestId: 'field-introMobileContainsPoliticalContent',
    politicalContentLabelTranslationKey: 'builder.formFields.containsPoliticalContent.introMobile',
    apiRoute: apiRoutes.creativeIntroMobileImage,
  },
  [HighImpactCreativeTypes.MASTHEAD]: {
    type: HighImpactCreativeTypes.MASTHEAD,
    fieldName: FieldTypes.CREATIVE_MASTHEAD_IMAGE,
    politicalContentFieldName: FieldTypes.MASTHEAD_CONTAINS_POLITICAL_CONTENT,
    dimensions: { width: 970, height: 250 },
    maxFileSize: 100 * 1024,
    translationKeyPrefix: 'builder.formFields.creativeMastheadImage',
    testId: 'field-creative-masthead-image',
    politicalContentTestId: 'field-mastheadContainsPoliticalContent',
    politicalContentLabelTranslationKey: 'builder.formFields.containsPoliticalContent.masthead',
    apiRoute: apiRoutes.creativeMastheadImage,
  },
  [HighImpactCreativeTypes.MASTHEAD_ADAPTIVE]: {
    type: HighImpactCreativeTypes.MASTHEAD_ADAPTIVE,
    fieldName: FieldTypes.CREATIVE_MASTHEAD_ADAPTIVE_IMAGE,
    politicalContentFieldName: FieldTypes.MASTHEAD_ADAPTIVE_CONTAINS_POLITICAL_CONTENT,
    dimensions: { width: 720, height: 240 },
    maxFileSize: 100 * 1024,
    translationKeyPrefix: 'builder.formFields.creativeMastheadAdaptiveImage',
    testId: 'field-creative-masthead-adaptive-image',
    politicalContentTestId: 'field-mastheadAdaptiveContainsPoliticalContent',
    politicalContentLabelTranslationKey: 'builder.formFields.containsPoliticalContent.mastheadAdaptive',
    apiRoute: apiRoutes.creativeMastheadAdaptiveImage,
  },
  [HighImpactCreativeTypes.SKIN]: {
    type: HighImpactCreativeTypes.SKIN,
    fieldName: FieldTypes.CREATIVE_SKIN_IMAGE,
    politicalContentFieldName: FieldTypes.SKIN_CONTAINS_POLITICAL_CONTENT,
    dimensions: { width: 1800, height: 840 },
    maxFileSize: 150 * 1024,
    translationKeyPrefix: 'builder.formFields.creativeSkinImage',
    testId: 'field-creative-skin-image',
    politicalContentTestId: 'field-skinContainsPoliticalContent',
    politicalContentLabelTranslationKey: 'builder.formFields.containsPoliticalContent.skin',
    apiRoute: apiRoutes.creativeSkinImage,
  },
};

// Insertion order above is the order the formats render in.
export const HIGH_IMPACT_CREATIVE_FORMAT_LIST = Object.values(HIGH_IMPACT_CREATIVE_FORMATS);

export const HIGH_IMPACT_CREATIVE_FIELD_NAMES = HIGH_IMPACT_CREATIVE_FORMAT_LIST.map(({ fieldName }) => fieldName);
